import parsePlanAmount from "./parsePlanAmount";

// Espelho de backend/src/services/CompanyService/parsePlanMetadata.ts -- manter
// os dois em sincronia.
//
// Lê as colunas do plano (recurrence, billingTypes) e só cai no TÍTULO como
// fallback, para o caso de uma linha antiga que a migration ainda não tocou.
// Diferente do backend, nada aqui lança erro: isto alimenta tela, e tela que
// explode por causa de um campo nulo derruba o app inteiro.
//
// Antes disso, as regras de nome estavam duplicadas em Signup/index.js e em
// Subscription/index.js, cada cópia com o comentário "Mirror of backend
// parsePlanMetadata.ts". Agora existe um lugar só.

export const PLAN_RECURRENCES = ["MENSAL", "SEMESTRAL", "ANUAL"];
export const PLAN_BILLING_TYPES = ["PIX", "BOLETO", "CREDIT_CARD"];

export const RECURRENCE_LABELS = {
  MENSAL: "Mensal",
  SEMESTRAL: "Semestral",
  ANUAL: "Anual"
};

export const BILLING_TYPE_LABELS = {
  PIX: "PIX",
  BOLETO: "Boleto",
  CREDIT_CARD: "Cartão de Crédito"
};

export const MONTHS_BY_RECURRENCE = {
  MENSAL: 1,
  SEMESTRAL: 6,
  ANUAL: 12
};

// "monthly" cai em null de propósito: era o placeholder que todo plano
// carregava, independente do ciclo real.
export const planRecurrence = (plan) => {
  const stored = String(plan?.recurrence || "")
    .trim()
    .toUpperCase();
  if (PLAN_RECURRENCES.includes(stored)) return stored;

  const name = plan?.name || "";
  const hasMensal = /\bmensal\b/i.test(name);
  const hasSemestral = /\bsemestral\b/i.test(name);
  const hasAnual = /\banual\b/i.test(name);
  if ([hasMensal, hasSemestral, hasAnual].filter(Boolean).length !== 1) {
    return null;
  }
  if (hasMensal) return "MENSAL";
  if (hasSemestral) return "SEMESTRAL";
  return "ANUAL";
};

export const recurrenceLabel = (plan) => {
  const recurrence = planRecurrence(plan);
  return recurrence ? RECURRENCE_LABELS[recurrence] : "—";
};

export const allowedPaymentMethods = (plan) => {
  if (!plan) return [];

  if (Array.isArray(plan.billingTypes)) {
    const stored = plan.billingTypes.map((t) =>
      String(t || "")
        .trim()
        .toUpperCase()
    );
    // Ordem canônica, sem repetição: um ["CREDIT_CARD","PIX"] gravado se
    // comporta igual a um ["PIX","CREDIT_CARD"].
    const canonical = PLAN_BILLING_TYPES.filter((t) => stored.includes(t));
    if (canonical.length) return canonical;
  }

  const name = plan.name || "";
  const methods = [];
  if (/\bpix\b/i.test(name)) methods.push("PIX");
  if (/\bboleto\b/i.test(name)) methods.push("BOLETO");
  if (/cart[aã]o/i.test(name)) methods.push("CREDIT_CARD");
  return methods;
};

// Plano pago em parcelas no Asaas: multi-mês E cartão como única forma de
// pagamento. Mensal + cartão é assinatura normal, não parcelamento.
export const isCardInstallmentPlan = (plan) => {
  const recurrence = planRecurrence(plan);
  if (!recurrence || recurrence === "MENSAL") return false;
  const methods = allowedPaymentMethods(plan);
  return (
    methods.includes("CREDIT_CARD") &&
    !methods.includes("PIX") &&
    !methods.includes("BOLETO")
  );
};

export const maxInstallmentCount = (plan) =>
  isCardInstallmentPlan(plan) ? MONTHS_BY_RECURRENCE[planRecurrence(plan)] : null;

// Valor do contrato, espelhando getPlanTotalValue (asaasCheckoutHelpers.ts).
//
// Plans.amount quer dizer duas coisas diferentes: nos planos "em até Nx" é o
// valor da PARCELA mensal (o plano 10 guarda 67,90), em todos os outros é o
// total do CICLO (o gêmeo dele no boleto, plano 9, guarda 407,40 -- e
// 67,90 x 6 = 407,40). Sem passar por aqui, dois clientes no mesmo contrato
// anual entram na conta com valores 12x diferentes.
export const planContractValue = (plan) => {
  const amount = parsePlanAmount(plan?.amount);
  const max = maxInstallmentCount(plan);
  return max ? Math.round(amount * max * 100) / 100 : amount;
};
